import { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Router from 'next/router';
import styled from 'styled-components';
import { debounce } from 'lodash';
import Button from 'src/components/buttons/button';
import signupContext from 'src/constants/signupContext';
import MessageEditor from 'src/components/editor/messageEditor';
import { showSignupModal, showModal } from 'src/modules/shared/modal/actions';
import { setAlert } from 'src/typescript/components/alert/actions';
import { userHasRole, userIsMemberOfCurrentGroup } from 'src/utils/context';
import { respondPost } from 'src/modules/community/singlePost/actions/actions';
import FollowButton from 'src/components/buttons/followButton';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import LazyImage from 'src/utils/lazyImage';
import modalTypes from 'src/constants/modalTypes';
import dynamic from 'next/dynamic';
const EmojiPicker = dynamic(() => import('./emojiPicker'), { ssr: false });
import RespondImageUpload from 'src/modules/community/singlePost/components/respondImageUpload';
import { showGuidelines } from 'src/modules/community/shared/components/communityGuidelinesModal';
import { EMAIL, PHONE_NUMBER } from 'src/constants/regex';

export class PostRespond extends Component {
  static propTypes = {
    autoFocus: PropTypes.bool,
    canCancel: PropTypes.bool,
    onCancel: PropTypes.func,
    post: PropTypes.object,
    signupContext: PropTypes.object,
    simple: PropTypes.bool,
    superPost: PropTypes.object,
  };

  static defaultProps = {
    onCancel: () => {},
    autoFocus: false,
    canCancel: false,
    signupContext: {
      section: signupContext.section.POST,
    },
  }

  constructor(props) {
    super(props);
    this.state = {
      bodyEdited: false,
      submitDisabled: false,
      showEmojiPicker: false,
      bold: false,
      italic: false,
      underline: false,
      previewImageHash: '',
      previewImageUrl: null,
      previewImageDescription: '',
    };
    this.messageEditorRef = createRef();
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    Router.events.on('beforeHistoryChange', this.handleBeforeHistoryChange);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    Router.events.off('beforeHistoryChange', this.handleBeforeHistoryChange);
  }

  // Displays popup if there is unsaved text and page is about to unload
  // (page refresh, close window or non-solaris link)
  handleBeforeUnload = (e) => {
    if (this.checkBodyHasChanged()) {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes, are you sure you want to leave?';
    }
  }

  // Displays popup if there is unsaved text and user links to other solaris page
  handleBeforeHistoryChange = (newRoute) => {
    if (this.checkBodyHasChanged() && !this.isSinglePostPage(newRoute) && !window.confirm(
      'If you leave this page the text you\'ve written will be lost.' +
        ' Click \'Cancel\' to stay on this page or \'OK\' to leave this page and lose what you\'ve written.',
    )) {
      throw new Error;
    }
  }

  isSinglePostPage(route) {
    const regex = /[^/]+\/posts\/\d+/;
    return !!route && regex.test(route);
  }

  checkBodyHasChanged() {
    if (!this.messageEditorRef.current) return false;
    const { str } = this.messageEditorRef.current.getData();
    return this.state.bodyEdited && !!str && str.trim().length > 0;
  }

  onSubmit = async () => {
    if (!this.messageEditorRef.current || this.state.submitDisabled) return;

    const { str, mentionedUsers } = this.messageEditorRef.current.getData();
    const { post, superPost, community, currentUser, showSignupModal, setAlert } = this.props;
    const isLoggedIn = !!currentUser;

    this.setState({ submitDisabled: true });

    if (str.length <= 1) {
      setAlert(i18n.t('Oops! you forgot to write a comment'));
      this.setState({ submitDisabled: false });
      return;
    }

    const replyData = {
      postId: post.id,
      superParentId: superPost.id,
      body: str,
      communitySlug: community.slug,
      mentionedUsers,
      imageHash: this.state.previewImageHash,
      imageDescription: this.state.previewImageDescription,
    };

    if (!isLoggedIn) {
      const replySignupContext = {
        element: signupContext.element.REPLY,
        section: this.props.signupContext.section,
      };
      showSignupModal(replySignupContext, community, false);
      trackFootfallEvent(footfallEventTypes.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: replySignupContext,
      });

      this.setState({ submitDisabled: false });
      return;
    }

    if (userHasRole(currentUser, 'Restricted')) {
      setAlert(i18n.t('You have been restricted in this community. Please contact this community\'s administrator to find out why.'));
      this.setState({ submitDisabled: false });
      return;
    }

    if (EMAIL.test(str) || PHONE_NUMBER.test(str) || this.state.previewImageHash) {
      return this.props.showModal({
        modalType: modalTypes.PRIVACY_REMINDER,
        modalProps: {
          onCancel: () => this.setState({ submitDisabled: false }),
          publishPost: this.respondToPost,
          replyData,
        },
      });
    }
    this.setState({ showEmojiPicker: false });
    // Make a response request
    this.respondToPost(replyData);
  }

  respondToPost = async (data) => {
    const response = await this.props.respondPost(data);

    if (response) {
      this.clear();
      trackFootfallEvent(footfallEventTypes.CLICKED, {clickType: 'completed-reply'});
    }
  }

  clear = () => {
    // Clear the textarea and blur
    if (!this.messageEditorRef.current) return false;
    this.messageEditorRef.current.clear();
    this.setState({
      bodyEdited: false,
      submitDisabled: false,
      previewImageHash: '',
      previewImageUrl: null,
      previewImageDescription: '',
    });

    // unfocus element
    if (document.activeElement) {
      document.activeElement.blur();
    }
    // Close the reply box
    this.props.onCancel();
  }

  focus = () => {
    if (!this.messageEditorRef.current) return;
    this.messageEditorRef.current.focus();
  }

  onChange = () => {
    if (!this.state.bodyEdited) this.setState({ bodyEdited: true });
  }

  startMention = () => {
    if (!this.messageEditorRef.current) return;
    this.messageEditorRef.current.startMention();
  }

  addEmoji = (emoji) => {
    if (!this.messageEditorRef.current) return;
    this.messageEditorRef.current.addEmoji(emoji);
    this.setState({ bodyEdited: true });
  }

  focusSelection = () => {
    this.messageEditorRef.current.focusNow();
    const sel = window.getSelection();
    const range = sel.getRangeAt(0);
    sel.addRange(range);
  }

  toggleFormat = (type) => {
    this.focusSelection();
    if (type) {
      document.execCommand(type, false, null);
      this.setState({ [type]: !this.state[type] });
    } else {
      this.setState({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
      });
    }
  }

  debouncedToggleFormat = debounce(this.toggleFormat, 200);

  renderReplyBox() {
    const { autoFocus, currentUser, community, showModal } = this.props;
    const { showEmojiPicker, submitDisabled, bodyEdited, previewImageUrl, previewImageDescription } = this.state;

    return (
      <Fragment>
        <MessageEditor
          autoFocus={autoFocus}
          className="respond__text-field input--text"
          currentUserId={currentUser && currentUser.userId}
          dataTestId="respond-text-field"
          disabled={submitDisabled}
          groupId={community.communityId}
          previewImageDescription={previewImageDescription}
          previewImageUrl={previewImageUrl}
          ref={this.messageEditorRef}
          onAddImageDescription={() => {
            showModal({
              modalType: modalTypes.REPLY_IMAGE_DESCRIPTION,
              modalProps: {
                imageDescription: previewImageDescription,
                onAccept: (newDescription) => this.setState({ previewImageDescription: newDescription }),
              },
            });
          }}
          onChange={this.onChange}
          onFocus={() => {
            if (showGuidelines(community)) {
              showModal({modalType: modalTypes.COMMUNITY_GUIDELINES});
            }
          }}
          onPreviewRemove={() => this.setState({
            previewImageHash: '', previewImageUrl: null, previewImageDescription: '',
          })}
        />

        <div className="respond__buttons">
          <IconButton
            aria-label="Add bold"
            className={this.state.bold ? 'btn active' : 'btn'}
            onClick={() => this.toggleFormat('bold')}
          >
            <span className="icon icon-bold" title="bold"/>
          </IconButton>
          <IconButton
            aria-label="Add italic"
            className={this.state.italic ? 'btn active' : 'btn'}
            onClick={() => this.toggleFormat('italic')}
          >
            <span className="icon icon-italic" title="italic"/>
          </IconButton>
          <IconButton
            aria-label="Add underline"
            className={this.state.underline ? 'btn active' : 'btn'}
            onClick={() => this.toggleFormat('underline')}
          >
            <span className="icon icon-underline" title="underline"/>
          </IconButton>
          <IconButton
            aria-label="Add mention"
            className="btn"
            onClick={() => {
              this.setState({ showEmojiPicker: false });
              this.startMention();
          }}>
            <span className="icon icon-at" title="mention" />
          </IconButton>
          <IconButton
            aria-label="Add emoji"
            className={showEmojiPicker ? 'btn active' : 'btn'}
            onClick={() => this.setState({ showEmojiPicker: !showEmojiPicker })}
          >
            <span className="icon icon-smile" title="emoji" />
          </IconButton>
          {!previewImageUrl &&
            <RespondImageUpload
              uid={this.props.respondImageUploadUid}
              onUploadSuccess={(previewImageHash, previewImageUrl) => {
                this.setState({ previewImageHash, previewImageUrl });
                trackFootfallEvent(footfallEventTypes.CLICKED, {clickType: 'image uploaded'});
                showModal({
                  modalType: modalTypes.REPLY_IMAGE_DESCRIPTION,
                  modalProps: {
                    imageDescription: previewImageDescription,
                    onAccept: (newDescription) => this.setState({ previewImageDescription: newDescription }),
                  },
                });
              }}
            />
          }
          <ReplyButton
            dataTestId="reply-box-reply-button"
            disabled={!bodyEdited || submitDisabled}
            text={i18n.t('Reply')}
            onClick={this.onSubmit}
          />
        </div>
        {showEmojiPicker && <EmojiPicker onSelect={this.addEmoji} />}
      </Fragment>
    );
  }

  render() {
    const { currentUser, community, post } = this.props;
    const isCommunityMember = userIsMemberOfCurrentGroup(currentUser, community);
    const isCurrentGroupMemberOrVisitor = !currentUser || isCommunityMember;
    const isPrivate = post.isPrivate && !isCommunityMember;

    const authorUsername = !post.author.isDeleted && !post.author.isHidden
      ? post.author.username
      : i18n.t('Hidden');

    if (isPrivate) return null;

    return (
      <div className="respond">
        <div className="respond__header">
          {currentUser &&
            <div className="respond__header-image">
              <LazyImage
                alt={currentUser.username}
                classnames="avatar avatar--small"
                src={currentUser.avatarImageUrl}
              />
            </div>
          }
          <div className="respond__header-action">
            <HeaderActionFirstRow>
              {i18n.t('Reply to')}{' '}{authorUsername}
              {this.props.canCancel &&
                <IconButton className="btn" onClick={this.props.onCancel}>
                  <span className="icon icon-cross-2" title="Close"/>
                </IconButton>
              }
            </HeaderActionFirstRow>
            {!isCurrentGroupMemberOrVisitor &&
              <FollowButton
                buttonType="none"
                community={this.props.community}
                followType="group"
                isActive={true}
                user={this.props.currentUser}
              />
            }
          </div>
        </div>
        {isCurrentGroupMemberOrVisitor && this.renderReplyBox()}
      </div>
    );
  }
}

const actions = {
  respondPost,
  showModal,
  showSignupModal,
  setAlert,
};

export default connect(null, actions, null, { forwardRef: true })(PostRespond);

const ReplyButton = styled(Button)`
  margin-left: auto;
`;
const HeaderActionFirstRow = styled.div`
  position: relative;
  button {
    position: absolute;
    right: -10px;
  }
`;
const IconButton = styled.button`
  margin: 0;
  padding: 0 10px;
  border: none;
  color: ${props => props.theme.colorGreyDark};
  font-size: 23px;
  transition: none;

  // iOS hover doesn't turn off
  &:hover { // set hover to default colour
    color: ${props => props.theme.colorGreyDark};
  }
  @media (hover: hover) { // only use hover colour if supported by browser
    &:hover {
      color: ${props => props.theme.colorBlue};
    }
  }
  &.active {
    color: ${props => props.theme.colorBlue};
  }

  @media (${props => props.theme.underScreenSmall}) {
    padding: 0 9px;
  }
`;
